import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import "./ProjectInfo.css";
import {
  Building2,
  MapPin,
  CalendarDays,
  Lock,
  ChevronDown,
  ChevronUp,
  PlusCircle,
} from "lucide-react";

const ProjectInfo = ({
  customers = [],
  estimateData = {},
  handleCustomerChange,
  handleInputChange,
  nextTab,
  prevTab,
  cancel,
}) => {
  const navigate = useNavigate();
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="pi-shell">
      <header className="pi-head">
        <div>
          <h2>Project Information</h2>
          <p className="muted">
            Select a customer and set dates to begin your estimate.
          </p>
        </div>
      </header>

      <section className="pi-grid">
        {/* LEFT: Form */}
        <div className="pi-form panel">
          {/* Customer */}
          <div className="field-row">
            <label htmlFor="customerName">
              Customer <span className="req">*</span>
            </label>
            <div className="combo">
              <select
                id="customerName"
                name="customerName"
                onChange={handleCustomerChange}
                defaultValue={estimateData?.customerId || ""}
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => navigate("/customers")}
                id="add-customer-button"
                className="btn-ghost small"
                title="Add new customer"
              >
                <PlusCircle size={16} /> Add
              </button>
            </div>
            <p className="hint">
              Choose an existing customer or create a new one.
            </p>
          </div>

          {/* Address (read-only) */}
          <div className="field-row">
            <label htmlFor="customerAddress">Customer Address</label>
            <div className="with-icon">
              <MapPin size={16} className="ico muted" />
              <input
                id="customerAddress"
                type="text"
                name="customerAddress"
                value={estimateData?.customerAddress || ""}
                placeholder="Address auto-fills after selecting a customer"
                readOnly
                className="readonly"
              />
              <Lock size={14} className="ico lock" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid-2">
            <div className="field-row">
              <label htmlFor="date">Date</label>
              <div className="with-icon">
                <CalendarDays size={16} className="ico muted" />
                <input
                  id="date"
                  type="date"
                  name="date"
                  value={estimateData?.date || ""}
                  readOnly
                  className="readonly"
                />
                <Lock size={14} className="ico lock" />
              </div>
            </div>

            <div className="field-row">
              <label htmlFor="expiryDate">
                Expiry Date <span className="req">*</span>
              </label>
              <div className="with-icon">
                <CalendarDays size={16} className="ico muted" />
                <input
                  id="expiryDate"
                  type="date"
                  name="expiryDate"
                  value={estimateData?.expiryDate || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <p className="hint">When should this estimate expire?</p>
            </div>
          </div>

          {/* Estimate no. (read-only) and Duration */}
          <div className="grid-2">
            <div className="field-row">
              <label htmlFor="estimateNumber">Estimate Number</label>
              <div className="with-icon">
                <Building2 size={16} className="ico muted" />
                <input
                  id="estimateNumber"
                  type="text"
                  name="estimateNumber"
                  value={estimateData?.estimateNumber || ""}
                  readOnly
                  className="readonly"
                />
                <Lock size={14} className="ico lock" />
              </div>
            </div>

            <div className="field-row">
              <label htmlFor="projectDuration">
                Project Duration (Days) <span className="req">*</span>
              </label>
              <input
                id="projectDuration"
                type="number"
                name="projectDuration"
                value={estimateData?.projectDuration || ""}
                onChange={handleInputChange}
                placeholder="e.g., 3"
              />
            </div>
          </div>

          {/* Advanced (optional) */}
          <div className="advanced">
            <button
              type="button"
              className="btn-ghost small"
              onClick={() => setShowAdvanced((s) => !s)}
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? (
                <ChevronUp size={14} />
              ) : (
                <ChevronDown size={14} />
              )}
              {showAdvanced ? "Hide" : "Show"} advanced details
            </button>

            {showAdvanced && (
              <div className="grid-3 advanced-grid">
                <div className="field-row">
                  <label htmlFor="projectName">Project Name</label>
                  <input
                    id="projectName"
                    type="text"
                    name="projectName"
                    value={estimateData?.projectName || ""}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                  />
                </div>

                <div className="field-row">
                  <label htmlFor="projectManager">Project Manager</label>
                  <input
                    id="projectManager"
                    type="text"
                    name="projectManager"
                    value={estimateData?.projectManager || ""}
                    onChange={handleInputChange}
                    placeholder="Manager's name"
                  />
                </div>

                <div className="field-row">
                  <label htmlFor="projectType">Project Type</label>
                  <input
                    id="projectType"
                    type="text"
                    name="projectType"
                    value={estimateData?.projectType || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., IT, Construction"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Nav buttons */}
          <div className="pi-actions">
            <button
              type="button"
              onClick={cancel}
              id="cancel-button"
              className="btn-ghost"
            >
              Cancel
            </button>
            {prevTab && (
              <button
                type="button"
                onClick={prevTab}
                id="prev-button"
                className="btn-ghost"
              >
                Prev
              </button>
            )}
            <button
              type="button"
              onClick={nextTab}
              id="next-button"
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>

        {/* RIGHT: Summary Card */}
        <aside className="pi-summary panel">
          <h4>Summary</h4>
          <ul className="kv">
            <li>
              <span>Customer</span>
              <strong>{estimateData?.customerName || "—"}</strong>
            </li>
            <li>
              <span>Address</span>
              <strong className="truncate">
                {estimateData?.customerAddress || "—"}
              </strong>
            </li>
            <li>
              <span>Date</span>
              <strong>{estimateData?.date || "—"}</strong>
            </li>
            <li>
              <span>Expiry</span>
              <strong>{estimateData?.expiryDate || "—"}</strong>
            </li>
            <li>
              <span>Estimate #</span>
              <strong>{estimateData?.estimateNumber || "—"}</strong>
            </li>
            <li>
              <span>Duration</span>
              <strong>{estimateData?.projectDuration || "—"} days</strong>
            </li>
          </ul>
          <p className="mini-note">
            You can edit these later in the next steps before finalizing.
          </p>
        </aside>
      </section>
    </div>
  );
};

ProjectInfo.propTypes = {
  customers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      company_name: PropTypes.string.isRequired,
    })
  ),
  estimateData: PropTypes.object,
  handleCustomerChange: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  nextTab: PropTypes.func.isRequired,
  prevTab: PropTypes.func,
  cancel: PropTypes.func.isRequired,
};

export default ProjectInfo;
